import React, { useState } from "react";
import axios from "axios";
import { Mail, Loader2, CheckCircle2 } from "lucide-react";
import { useLang, useSettings } from "../lib/providers";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Inline newsletter signup form. Used in the footer and the homepage section.
 *
 * Variants:
 *  - "footer"   : compact, single line on desktop
 *  - "section"  : larger, centered, full marketing copy
 *  - "popup"    : same fields but tighter, used inside the exit-intent modal
 */
export default function NewsletterSignup({ variant = "section", source = "section" }) {
  const { lang } = useLang();
  const { settings } = useSettings();
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle"); // idle | loading | success | error
  const [error, setError] = useState("");
  // Master switch — hide the entire component when newsletter is disabled in CMS.
  // NOTE: this early return MUST come after every hook to respect Rules of Hooks.
  if (settings?.newsletter_enabled === false) return null;
  const pct = settings?.newsletter_promo_percent || 10;
  const minOrder = Number(settings?.newsletter_promo_min_order_dzd || 0);
  const minLabel = minOrder > 0
    ? (lang === "ar"
        ? `الكود صالح ابتداء من ${minOrder.toLocaleString("fr-FR")} دج من المشتريات`
        : `Code valable dès ${minOrder.toLocaleString("fr-FR")} DZD d'achat`)
    : "";

  const submit = async (e) => {
    e?.preventDefault?.();
    setError("");
    const value = (email || "").trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      setError(lang === "ar" ? "بريد إلكتروني غير صالح" : "Email invalide");
      return;
    }
    setState("loading");
    try {
      await axios.post(`${API}/newsletter/subscribe`, { email: value, source, lang });
      setState("success");
    } catch (err) {
      const msg = err?.response?.data?.detail || (lang === "ar" ? "خطأ، حاول مرة أخرى" : "Erreur, réessayez");
      setError(typeof msg === "string" ? msg : "Erreur");
      setState("idle");
    }
  };

  if (state === "success") {
    return (
      <div
        className={
          variant === "footer"
            ? "flex items-center gap-2 text-xs text-neon font-bold"
            : "border border-neon bg-neon/10 p-4 sm:p-6 text-center"
        }
        data-testid="newsletter-success"
      >
        <CheckCircle2 className="w-5 h-5 text-neon shrink-0 inline-block mr-2" />
        <span className="text-sm sm:text-base font-bold">
          {lang === "ar"
            ? "✅ تحقق من بريدك الإلكتروني (وكذلك مجلد البريد العشوائي إذا كنت تستخدم Hotmail / Outlook) لتأكيد اشتراكك واستلام كود الخصم !"
            : "✅ Vérifiez votre boîte mail (et vos spams si vous utilisez Hotmail / Outlook) pour confirmer votre inscription et recevoir votre code promo !"}
        </span>
      </div>
    );
  }

  // === SECTION variant (homepage section) ===
  if (variant === "section") {
    return (
      <section
        id="newsletter-section"
        className="py-16 sm:py-20 bg-gradient-to-br from-neon/10 via-black to-black border-y border-neon/30"
        data-testid="newsletter-section"
      >
        <div className="max-w-2xl mx-auto px-4 sm:px-6 text-center">
          <p className="text-[10px] uppercase tracking-widest text-neon font-bold mb-3">
            🎁 {lang === "ar" ? "عرض خاص للأعضاء الجدد" : "Offre membres exclusifs"}
          </p>
          <h2 className="font-heading font-black uppercase tracking-tight text-3xl sm:text-4xl mb-3">
            {lang === "ar"
              ? `اشترك واحصل على -${pct}% فورا`
              : `Inscrivez-vous, recevez -${pct}% immédiatement`}
          </h2>
          <p className="text-sm sm:text-base text-white/70 mb-2 max-w-xl mx-auto">
            {lang === "ar"
              ? "كود الخصم الخاص بك على طلبك القادم + نصائح حصرية + إطلاق منتجات قبل الجميع."
              : "Votre code promo personnel sur votre prochaine commande + conseils exclusifs + lancements en avant-première."}
          </p>
          {minLabel && (
            <p className="text-xs text-neon/90 font-bold mb-6" data-testid="newsletter-min-order-section">
              ⚠️ {minLabel}
            </p>
          )}
          <form onSubmit={submit} className="flex flex-col sm:flex-row gap-2 max-w-md mx-auto" dir={lang === "ar" ? "rtl" : "ltr"}>
            <div className="flex-1 relative">
              <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder={lang === "ar" ? "بريدك الإلكتروني" : "Votre email"}
                className="w-full bg-black/60 border border-white/15 pl-10 pr-3 py-3 text-sm focus:border-neon outline-none"
                data-testid="newsletter-email-section"
                autoComplete="email"
              />
            </div>
            <button
              type="submit"
              disabled={state === "loading"}
              className="bg-neon text-black hover:opacity-90 px-6 py-3 font-black uppercase tracking-widest text-sm disabled:opacity-50"
              data-testid="newsletter-submit-section"
            >
              {state === "loading" ? <Loader2 className="w-4 h-4 animate-spin inline-block" /> : (lang === "ar" ? "أنا أشترك" : "Je m'inscris")}
            </button>
          </form>
          {error && <p className="text-xs text-clay mt-2" data-testid="newsletter-error-section">{error}</p>}
          <p className="text-[10px] text-white/40 mt-4">
            {lang === "ar"
              ? "بالاشتراك، أوافق على تلقي رسائل البريد الإلكتروني الترويجية. إلغاء الاشتراك بنقرة واحدة في كل بريد إلكتروني."
              : "En vous inscrivant, vous acceptez de recevoir nos emails. Désinscription en 1 clic dans chaque email."}
          </p>
        </div>
      </section>
    );
  }

  // === FOOTER variant ===
  if (variant === "footer") {
    return (
      <div className="space-y-2" data-testid="newsletter-footer">
        <p className="text-[10px] uppercase tracking-widest text-neon font-bold">
          🎁 {lang === "ar" ? "-10% على طلبك الأول" : "-10% sur votre 1ère commande"}
        </p>
        <form onSubmit={submit} className="flex gap-1.5" dir={lang === "ar" ? "rtl" : "ltr"}>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            placeholder={lang === "ar" ? "بريدك الإلكتروني" : "Votre email"}
            className="flex-1 bg-black/60 border border-white/15 px-3 py-2 text-xs focus:border-neon outline-none"
            data-testid="newsletter-email-footer"
            autoComplete="email"
          />
          <button
            type="submit"
            disabled={state === "loading"}
            className="bg-neon text-black hover:opacity-90 px-3 py-2 font-black uppercase tracking-widest text-[10px] disabled:opacity-50"
            data-testid="newsletter-submit-footer"
          >
            {state === "loading" ? "..." : (lang === "ar" ? "اشتراك" : "OK")}
          </button>
        </form>
        {error && <p className="text-[10px] text-clay" data-testid="newsletter-error-footer">{error}</p>}
      </div>
    );
  }

  // === POPUP variant ===
  return (
    <form onSubmit={submit} className="space-y-3" dir={lang === "ar" ? "rtl" : "ltr"}>
      <input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
        placeholder={lang === "ar" ? "بريدك الإلكتروني" : "Votre email"}
        className="w-full bg-black/60 border border-white/15 px-3 py-3 text-sm focus:border-neon outline-none"
        data-testid="newsletter-email-popup"
        autoFocus
        autoComplete="email"
      />
      <button
        type="submit"
        disabled={state === "loading"}
        className="w-full bg-neon text-black hover:opacity-90 px-6 py-3 font-black uppercase tracking-widest text-sm disabled:opacity-50"
        data-testid="newsletter-submit-popup"
      >
        {state === "loading" ? <Loader2 className="w-4 h-4 animate-spin inline-block" /> : (lang === "ar" ? "🎁 احصل على الكود" : "🎁 Je veux mon code promo")}
      </button>
      {minLabel && (
        <p className="text-[10px] text-neon/80 text-center" data-testid="newsletter-min-order-popup">⚠️ {minLabel}</p>
      )}
      {error && <p className="text-xs text-clay" data-testid="newsletter-error-popup">{error}</p>}
    </form>
  );
}
